import { FlagBanner } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ItemLite } from "@plannello/shared";
import { memo } from "react";
import { AvatarStack } from "@/components/domain/AvatarStack";
import { FieldChip } from "@/components/domain/FieldChip";
import { ItemKey } from "@/components/domain/ItemKey";
import { PriorityIcon } from "@/components/domain/PriorityIcon";
import { StatusIcon } from "@/components/domain/StatusIcon";
import { TypeIcon } from "@/components/domain/TypeIcon";
import { itemChips } from "@/components/items/display";
import type { Catalog } from "@/data/catalog";
import { TranslatedMark, titleTooltip, useDisplayTitle } from "@/data/titles";
import { cx } from "@/lib/cx";
import { loc } from "@/lib/format";

/**
 * One item as a dense line for planning surfaces (Next up, the timeline's unscheduled panel):
 * priority, key, type, status, title, then the first Field value, milestone and assignees.
 * Columns on the right hide first as the container narrows (container queries).
 */
export const ItemLine = memo(function ItemLine({
  item,
  catalog,
  keyWidth,
  showMilestone = true,
  compact,
  className,
}: {
  item: ItemLite;
  catalog: Catalog;
  keyWidth?: number;
  showMilestone?: boolean;
  /** Narrow panels: no chips, milestone or assignee. */
  compact?: boolean;
  className?: string;
}) {
  const shownTitle = useDisplayTitle(item);
  const status = catalog.statusById.get(item.statusId);
  const type = catalog.typeById.get(item.typeId);
  const chip = compact ? undefined : itemChips(item, catalog)[0];
  const milestone =
    !compact && showMilestone && item.milestoneId ? catalog.milestoneById.get(item.milestoneId) : undefined;
  const people = compact ? [] : item.assigneeIds.map((id) => catalog.userById.get(id)).filter((u) => !!u);
  return (
    <span data-item-id={item.id} className={cx("flex min-w-0 flex-1 items-center gap-2 text-sm", className)}>
      <PriorityIcon value={item.priority} />
      <ItemKey value={item.key} width={keyWidth} />
      {type ? (
        <TypeIcon
          name={type.icon}
          color={type.color}
          label={loc(type.name)}
          className="shrink-0 text-fg-subtle @max-md:hidden"
        />
      ) : null}
      {status ? <StatusIcon category={status.category} label={loc(status.name)} /> : null}
      <span className="min-w-0 flex-1 truncate-title text-fg" title={titleTooltip(shownTitle, item.title)}>
        {shownTitle.text}
        <TranslatedMark d={shownTitle} />
      </span>
      {chip ? <FieldChip label={chip.label} color={chip.color} field={chip.field} className="@max-2xl:hidden" /> : null}
      {milestone ? (
        <span
          className="flex max-w-28 shrink-0 items-center gap-1 text-xs text-fg-muted @max-xl:hidden"
          title={milestone.name}
        >
          <FlagBanner size={12} aria-hidden="true" className="shrink-0 text-fg-subtle" />
          <span className="sr-only">{m.prop_milestone()}: </span>
          <span className="truncate-title">{milestone.version ?? milestone.name}</span>
        </span>
      ) : null}
      {compact ? null : (
        <span className="flex w-8 shrink-0 justify-end">
          <AvatarStack people={people} size="sm" />
        </span>
      )}
    </span>
  );
});
